import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { PageMeta } from '@/components/seo/PageMeta'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { BrandGuidelinesEditor } from '@/components/brand-compliance'
import { brandComplianceService } from '@/lib/api/services/brandCompliance'
import type {
  BrandGuideline,
  ComplianceStrictness,
  CreateBrandGuidelineRequest,
} from '@/lib/api/types'
import {
  Plus,
  RefreshCw,
  Shield,
  Loader2,
  AlertCircle,
  MoreVertical,
  Pencil,
  Trash2,
  Star,
  Type,
  Palette,
  Image,
  Heading,
  Building2,
  X,
  ShieldAlert,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/contexts/auth-context'

interface TenantOption {
  id: string
  name: string
}

export default function BrandGuidelinesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tenantId = searchParams.get('tenantId')
  const [tenantName, setTenantName] = useState<string | null>(null)
  const [tenants, setTenants] = useState<TenantOption[]>([])
  const { user: currentUser, hasPermission, hasAnyRole, isLoading: authLoading } = useAuth()

  // Check if user has admin access
  const canAccessPage = currentUser?.isSuperAdmin || hasAnyRole(['Admin', 'Tenant Admin']) || hasPermission('brandguideline.view')

  const [guidelines, setGuidelines] = useState<BrandGuideline[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [deleteGuideline, setDeleteGuideline] = useState<BrandGuideline | null>(null)
  const [editingGuidelineId, setEditingGuidelineId] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Show access denied if user doesn't have permission
  if (!canAccessPage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="rounded-full bg-destructive/10 p-4 mb-4">
          <ShieldAlert className="h-12 w-12 text-destructive" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
        <p className="text-muted-foreground max-w-md mb-6">
          You don't have permission to access the Brand Guidelines page. Please contact your administrator if you need access.
        </p>
        <Button variant="outline" onClick={() => window.history.back()}>
          Go Back
        </Button>
      </div>
    )
  }

  // Create form state
  const [createForm, setCreateForm] = useState<CreateBrandGuidelineRequest>({
    name: '',
    description: '',
    strictness: 'Moderate',
    isDefault: false,
  })

  // Load tenants for filter dropdown
  useEffect(() => {
    const loadTenants = async () => {
      try {
        const { tenantsService } = await import('@/lib/api/services/tenants')
        const response = await tenantsService.getTenants({ pageSize: 100, sortBy: 'name' })
        setTenants(response.items?.map((t: { id: string; name: string }) => ({ id: t.id, name: t.name })) || [])
      } catch {
        // Ignore error
      }
    }
    loadTenants()
  }, [])

  // Load tenant name when filtering by tenant
  useEffect(() => {
    const loadTenantName = async () => {
      if (tenantId) {
        try {
          const { tenantsService } = await import('@/lib/api/services/tenants')
          const tenant = await tenantsService.getTenant(tenantId)
          setTenantName(tenant.name)
        } catch {
          // Ignore error
        }
      }
    }
    loadTenantName()
  }, [tenantId])

  // Clear tenant filter
  const clearTenantFilter = () => {
    searchParams.delete('tenantId')
    setSearchParams(searchParams)
    setTenantName(null)
  }

  // Set tenant filter
  const setTenantFilter = (id: string) => {
    if (id === 'all') {
      clearTenantFilter()
    } else {
      searchParams.set('tenantId', id)
      setSearchParams(searchParams)
    }
  }

  const loadGuidelines = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await brandComplianceService.getGuidelines(tenantId || undefined)
      setGuidelines(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load guidelines')
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => {
    loadGuidelines()
  }, [loadGuidelines])

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await loadGuidelines()
    } finally {
      setRefreshing(false)
    }
  }

  const handleCreate = async () => {
    if (!createForm.name.trim()) return

    setCreating(true)
    try {
      await brandComplianceService.createGuideline(createForm)
      await loadGuidelines()
      setCreateDialogOpen(false)
      setCreateForm({
        name: '',
        description: '',
        strictness: 'Moderate',
        isDefault: false,
      })
    } catch (err) {
      console.error('Failed to create guideline:', err)
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteGuideline) return

    setDeleting(true)
    try {
      await brandComplianceService.deleteGuideline(deleteGuideline.id)
      await loadGuidelines()
      setDeleteGuideline(null)
    } catch (err) {
      console.error('Failed to delete guideline:', err)
    } finally {
      setDeleting(false)
    }
  }

  const handleSetDefault = async (guideline: BrandGuideline) => {
    try {
      await brandComplianceService.updateGuideline(guideline.id, { isDefault: true })
      await loadGuidelines()
    } catch (err) {
      console.error('Failed to set default:', err)
    }
  }

  const handleToggleActive = async (guideline: BrandGuideline) => {
    try {
      await brandComplianceService.updateGuideline(guideline.id, {
        isActive: !guideline.isActive,
      })
      await loadGuidelines()
    } catch (err) {
      console.error('Failed to toggle active:', err)
    }
  }

  const getStrictnessColor = (strictness: ComplianceStrictness) => {
    switch (strictness) {
      case 'Strict':
        return 'text-red-600 dark:text-red-400'
      case 'Moderate':
        return 'text-amber-600 dark:text-amber-400'
      case 'Lenient':
        return 'text-green-600 dark:text-green-400'
    }
  }

  const getStrictnessBadge = (strictness: ComplianceStrictness) => {
    const styles = {
      Strict: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
      Moderate: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
      Lenient: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
    }
    return <Badge className={cn('text-xs', styles[strictness])}>{strictness}</Badge>
  }

  // If editing a guideline, show the editor
  if (editingGuidelineId) {
    return (
      <>
        <PageMeta
          title="Edit Brand Guideline | Admin"
          description="Configure brand compliance rules"
        />
        <div className="container mx-auto py-8 px-4 max-w-6xl">
          <BrandGuidelinesEditor
            guidelineId={editingGuidelineId}
            onBack={() => setEditingGuidelineId(null)}
            onUpdated={loadGuidelines}
          />
        </div>
      </>
    )
  }

  const activeCount = guidelines.filter((g) => g.isActive).length
  const totalRules = guidelines.reduce(
    (acc, g) => acc + g.fontRuleCount + g.colorRuleCount + g.logoRuleCount + g.styleRuleCount,
    0
  )

  return (
    <>
      <PageMeta
        title="Brand Guidelines | Admin"
        description="Manage brand compliance guidelines and rules"
      />

      <div className="container mx-auto py-8 px-4 max-w-6xl">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                Brand Guidelines
              </h1>
              <p className="text-neutral-500 dark:text-neutral-400 mt-1">
                {tenantId ? `Brand guidelines for ${tenantName || 'selected company'}` : 'Define brand compliance rules for typography, colors, logos, and styles'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Company Filter */}
              <Select value={tenantId || 'all'} onValueChange={setTenantFilter}>
                <SelectTrigger className="w-[200px]">
                  <Building2 className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="All Companies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={loading || refreshing}
              >
                <RefreshCw className={cn('h-4 w-4 mr-2', refreshing && 'animate-spin')} />
                Refresh
              </Button>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Guideline
              </Button>
            </div>
          </div>

          {/* Tenant Filter Banner */}
          {tenantId && (
            <Alert className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
              <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="flex items-center justify-between">
                <span>
                  Showing brand guidelines for company:{' '}
                  <Link
                    to={`/admin/companies/${tenantId}`}
                    className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {tenantName || tenantId}
                  </Link>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearTenantFilter}
                  className="h-7 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear filter
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Stats */}
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
              <Shield className="h-4 w-4 text-neutral-500" />
              <span className="text-sm text-neutral-600 dark:text-neutral-300">
                <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                  {guidelines.length}
                </span>{' '}
                guideline{guidelines.length !== 1 ? 's' : ''}
              </span>
            </div>
            <Badge variant="success" className="py-2">
              {activeCount} active
            </Badge>
            <Badge variant="secondary" className="py-2">
              {totalRules} total rules
            </Badge>
          </div>

          {/* Error State */}
          {error && !loading && (
            <div className="p-4 rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/50 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
              <Button variant="outline" size="sm" onClick={loadGuidelines}>
                Retry
              </Button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
              <p className="text-neutral-500 dark:text-neutral-400">Loading guidelines...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && guidelines.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 gap-4 border border-dashed border-neutral-200 dark:border-neutral-700 rounded-lg">
              <Shield className="h-12 w-12 text-neutral-400" />
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                No brand guidelines configured
              </h3>
              <p className="text-neutral-500 dark:text-neutral-400 text-center max-w-md">
                Create your first brand guideline to define typography, color, logo, and style
                rules for template compliance checking.
              </p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Guideline
              </Button>
            </div>
          )}

          {/* Guidelines List */}
          {!loading && guidelines.length > 0 && (
            <div className="grid gap-4 md:grid-cols-2">
              {guidelines.map((guideline) => (
                <Card key={guideline.id} className="relative">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            'p-2 rounded-lg',
                            guideline.isActive
                              ? 'bg-neutral-100 dark:bg-neutral-800'
                              : 'bg-neutral-50 dark:bg-neutral-900'
                          )}
                        >
                          <Shield
                            className={cn(
                              'h-5 w-5',
                              guideline.isActive
                                ? getStrictnessColor(guideline.strictness)
                                : 'text-neutral-400'
                            )}
                          />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <CardTitle
                              className={cn(
                                'text-base',
                                !guideline.isActive && 'text-neutral-500'
                              )}
                            >
                              {guideline.name}
                            </CardTitle>
                            {guideline.isDefault && (
                              <Badge variant="secondary" className="text-xs">
                                <Star className="h-3 w-3 mr-1" />
                                Default
                              </Badge>
                            )}
                          </div>
                          {guideline.description && (
                            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
                              {guideline.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={guideline.isActive}
                          onCheckedChange={() => handleToggleActive(guideline)}
                        />
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48 rounded-xl">
                            <DropdownMenuItem onClick={() => setEditingGuidelineId(guideline.id)} className="rounded-lg gap-2">
                              <Pencil className="h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            {!guideline.isDefault && (
                              <DropdownMenuItem onClick={() => handleSetDefault(guideline)} className="rounded-lg gap-2">
                                <Star className="h-4 w-4" />
                                Set as Default
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600 dark:text-red-400 rounded-lg focus:bg-red-50 dark:focus:bg-red-900/20 gap-2"
                              onClick={() => setDeleteGuideline(guideline)}
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3 mb-3">
                      {getStrictnessBadge(guideline.strictness)}
                      {!guideline.isActive && (
                        <Badge variant="outline" className="text-xs text-neutral-500">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-3 text-center">
                      <div className="p-2 bg-neutral-50 dark:bg-neutral-800/50 rounded">
                        <Type className="h-4 w-4 mx-auto text-neutral-500 mb-1" />
                        <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                          {guideline.fontRuleCount}
                        </span>
                        <span className="block text-xs text-neutral-500">Fonts</span>
                      </div>
                      <div className="p-2 bg-neutral-50 dark:bg-neutral-800/50 rounded">
                        <Palette className="h-4 w-4 mx-auto text-neutral-500 mb-1" />
                        <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                          {guideline.colorRuleCount}
                        </span>
                        <span className="block text-xs text-neutral-500">Colors</span>
                      </div>
                      <div className="p-2 bg-neutral-50 dark:bg-neutral-800/50 rounded">
                        <Image className="h-4 w-4 mx-auto text-neutral-500 mb-1" />
                        <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                          {guideline.logoRuleCount}
                        </span>
                        <span className="block text-xs text-neutral-500">Logos</span>
                      </div>
                      <div className="p-2 bg-neutral-50 dark:bg-neutral-800/50 rounded">
                        <Heading className="h-4 w-4 mx-auto text-neutral-500 mb-1" />
                        <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                          {guideline.styleRuleCount}
                        </span>
                        <span className="block text-xs text-neutral-500">Styles</span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full mt-4"
                      onClick={() => setEditingGuidelineId(guideline.id)}
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Configure Rules
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={(open) => !open && setCreateDialogOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Brand Guideline</DialogTitle>
            <DialogDescription>
              Create a new brand guideline to define compliance rules for your templates.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                placeholder="e.g., Corporate Brand 2024"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Optional description"
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Strictness Level</Label>
              <Select
                value={createForm.strictness}
                onValueChange={(value) =>
                  setCreateForm({ ...createForm, strictness: value as ComplianceStrictness })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Strict">
                    <div>
                      <span className="font-medium">Strict</span>
                      <span className="text-xs text-neutral-500 ml-2">Block on any violation</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Moderate">
                    <div>
                      <span className="font-medium">Moderate</span>
                      <span className="text-xs text-neutral-500 ml-2">Block on errors only</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Lenient">
                    <div>
                      <span className="font-medium">Lenient</span>
                      <span className="text-xs text-neutral-500 ml-2">Show warnings only</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
              <div>
                <Label>Set as Default</Label>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Use this guideline for all compliance checks
                </p>
              </div>
              <Switch
                checked={createForm.isDefault}
                onCheckedChange={(checked) => setCreateForm({ ...createForm, isDefault: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating || !createForm.name.trim()}>
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Guideline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteGuideline} onOpenChange={(open) => !open && setDeleteGuideline(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Brand Guideline</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteGuideline?.name}"? This will remove all
              associated rules. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteGuideline(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
